// Hover intent, modelled on the hoverIntent jQuery plugin. The menu opens when the
// pointer slows down over a trigger, not after a fixed wait. A pointer that only
// crosses a trigger keeps moving, so that menu never opens, however slowly the
// pointer travels. A pointer that stops opens the menu on the next sample.
const INTENT_INTERVAL = 100; // ms between pointer samples while deciding to close
const INTENT_SENSITIVITY = 7; // px of travel between samples that still counts as stopped

// Opening samples twice as often, so a menu opens about 50ms after the pointer settles
// rather than 100ms. With the same travel allowance per sample, a pointer that is still
// slowing down (under ~140px/s) also counts as settled.
const OPEN_INTERVAL = 50;

// Settled samples needed before an open panel closes while the pointer is still inside
// the header. Two samples tolerate a brief pause during the trip to the panel's links,
// and still close promptly once the pointer rests on another header item.
const HOLD_SETTLED_SAMPLES = 2;

// Last known pointer position, used to hold an open panel while the pointer is still
// crossing the header. One passive listener keeps this current for every menu.
let lastPointerX = -1;
let lastPointerY = -1;
document.addEventListener(
  'mousemove',
  (event) => {
    lastPointerX = event.clientX;
    lastPointerY = event.clientY;
  },
  { passive: true }
);

// One page dim + blur overlay shared by every menu. CSS in component-mega-menu.css shows it
// whenever a panel is open. It lives on <body>, because the header section's
// contain: layout would trap a fixed-position child inside the header.
if (!document.querySelector('.mega-menu-overlay')) {
  const overlay = document.createElement('div');
  overlay.className = 'mega-menu-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  document.body.appendChild(overlay);
}

class HeaderMegaMenu extends HTMLElement {
  constructor() {
    super();
    this._leaveTimer = null;
    this._enterTimer = null;
    this._priorX = 0;
    this._priorY = 0;
    this._currentX = 0;
    this._currentY = 0;
    this._trackPointer = (event) => {
      this._currentX = event.clientX;
      this._currentY = event.clientY;
    };
  }

  connectedCallback() {
    this.trigger = this.querySelector('.mega-menu__trigger');
    this.headerWrapper = document.querySelector('.header-wrapper');

    this.addEventListener('mouseenter', this.onPointerEnter.bind(this));
    this.addEventListener('mouseleave', this.onPointerLeave.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    this.trigger?.addEventListener('click', this.onTriggerClick.bind(this));

    // Hover-swap feature image: each link shows its own image, and the default returns
    // once the pointer leaves the link area.
    this.swap = this.querySelector('[data-mega-swap]');
    if (this.swap) {
      this.querySelectorAll('[data-mega-swap-target]').forEach((link) => {
        const show = () => this.showSwapImage(link.dataset.megaSwapTarget);
        link.addEventListener('mouseenter', show);
        link.addEventListener('focus', show);
      });
      this.querySelector('.mega-menu__groups')?.addEventListener('mouseleave', () => this.showSwapImage());
    }

    requestAnimationFrame(() => this.setHeaderBottomVar());
  }

  onTriggerClick(event) {
    const href = this.trigger?.getAttribute('href')?.trim();
    if (!href || href === '#' || href === '#top') {
      event.preventDefault();
    }
  }

  showSwapImage(key = 'default') {
    if (!this.swap) return;
    const images = this.swap.querySelectorAll('[data-mega-swap-key]');
    const hasImage = Array.from(images).some((image) => image.dataset.megaSwapKey === key);
    const activeKey = hasImage ? key : 'default';
    images.forEach((image) => image.classList.toggle('is-active', image.dataset.megaSwapKey === activeKey));
  }

  setHeaderPreventHide(active) {
    const wrap = this.headerWrapper || document.querySelector('.header-wrapper');
    if (wrap) wrap.preventHide = active;
  }

  clearEnterTimer() {
    if (this._enterTimer) {
      clearTimeout(this._enterTimer);
      this._enterTimer = null;
    }
    this.removeEventListener('mousemove', this._trackPointer);
  }

  clearLeaveTimer() {
    if (this._leaveTimer) {
      clearTimeout(this._leaveTimer);
      this._leaveTimer = null;
    }
  }

  open() {
    this.classList.add('mega-menu--interacting');
    this.closeOtherMegas();
    this.removeAttribute('data-force-closed');
    this.setExpanded(true);
    this.setHeaderPreventHide(true);
    this.setHeaderBottomVar();
  }

  onPointerEnter(event) {
    this.clearLeaveTimer();
    this.clearEnterTimer();
    // Sample the pointer from where it entered. onPointerLeave stops the sampling,
    // so a pointer that crosses this trigger never opens it. The menu that is
    // already open stays open, because closeOtherMegas runs inside open().
    this._priorX = event.clientX;
    this._currentX = event.clientX;
    this._priorY = event.clientY;
    this._currentY = event.clientY;
    this.addEventListener('mousemove', this._trackPointer);
    this._enterTimer = setTimeout(() => this.comparePointerSpeed(), OPEN_INTERVAL);
  }

  comparePointerSpeed() {
    this._enterTimer = null;
    const travelX = this._priorX - this._currentX;
    const travelY = this._priorY - this._currentY;

    // The pointer has settled, so the hover is deliberate.
    if (Math.sqrt(travelX * travelX + travelY * travelY) < INTENT_SENSITIVITY) {
      this.clearEnterTimer();
      this.open();
      return;
    }

    // The pointer is still travelling. Sample again from its current position.
    this._priorX = this._currentX;
    this._priorY = this._currentY;
    this._enterTimer = setTimeout(() => this.comparePointerSpeed(), OPEN_INTERVAL);
  }

  closeOtherMegas() {
    document.querySelectorAll('header-mega-menu').forEach((host) => {
      if (host !== this) host.close();
    });
  }

  pointerIsInHeader() {
    const wrap = this.headerWrapper || document.querySelector('.header-wrapper');
    if (!wrap) return false;
    const bounds = wrap.getBoundingClientRect();
    return (
      lastPointerX >= bounds.left &&
      lastPointerX <= bounds.right &&
      lastPointerY >= bounds.top &&
      lastPointerY <= bounds.bottom
    );
  }

  scheduleClose() {
    this.clearLeaveTimer();
    this._settledSamples = 0;
    this._holdPriorX = lastPointerX;
    this._holdPriorY = lastPointerY;

    // A pointer that has already left the header has finished with the panel, so it
    // closes at once. This keeps leaving the panel itself feeling immediate. A pointer
    // still inside the header gets its speed sampled first.
    const delay = this.pointerIsInHeader() ? INTENT_INTERVAL : 0;
    this._leaveTimer = setTimeout(() => this.considerClose(), delay);
  }

  considerClose() {
    this._leaveTimer = null;
    if (this.matches(':hover') || this.contains(document.activeElement)) return;

    if (this.pointerIsInHeader()) {
      const travelX = this._holdPriorX - lastPointerX;
      const travelY = this._holdPriorY - lastPointerY;
      this._holdPriorX = lastPointerX;
      this._holdPriorY = lastPointerY;

      if (Math.sqrt(travelX * travelX + travelY * travelY) < INTENT_SENSITIVITY) {
        this._settledSamples += 1;
      } else {
        this._settledSamples = 0;
      }

      // The pointer is still crossing the header, so the visitor is probably still
      // travelling to this panel's links. The sublinks sit far to the right of the
      // trigger, and that trip takes longer than any fixed delay can predict.
      if (this._settledSamples < HOLD_SETTLED_SAMPLES) {
        this._leaveTimer = setTimeout(() => this.considerClose(), INTENT_INTERVAL);
        return;
      }

      // The pointer has stopped on something else in the header, such as another nav
      // link, the logo or the cart. The visitor has moved on, so close the panel.
    }

    this.classList.remove('mega-menu--interacting');
    this.setExpanded(false);
    this.setHeaderPreventHide(false);
  }

  onPointerLeave() {
    this.clearEnterTimer();
    // A mouse click leaves focus on the trigger or a link. Drop it, or the focus would
    // hold the panel open after the pointer has gone. Keyboard focus stays.
    const focused = document.activeElement;
    if (focused && this.contains(focused) && !focused.matches(':focus-visible')) focused.blur();
    this.scheduleClose();
  }

  onFocusIn(event) {
    // Only keyboard focus opens the panel. Focus from a mouse click is handled by hover.
    if (!event.target.matches(':focus-visible')) return;
    this.clearLeaveTimer();
    this.clearEnterTimer();
    // Keyboard focus is always deliberate, so it opens the panel immediately.
    this.open();
  }

  // Escape closes the panel and returns focus to its trigger. The panel stays closed while
  // the trigger keeps focus (data-force-closed); tabbing back onto the trigger reopens it.
  onKeyDown(event) {
    if (event.key !== 'Escape' || this.trigger?.getAttribute('aria-expanded') !== 'true') return;
    event.preventDefault();
    // Focus first: focusing the trigger runs onFocusIn, which would reopen a closed panel.
    this.trigger.focus();
    this.close();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.matches(':hover')) {
        this.classList.remove('mega-menu--interacting');
        this.setExpanded(false);
        this.setHeaderPreventHide(false);
      }
    });
  }

  setExpanded(open) {
    if (!open) this.showSwapImage();
    if (!this.trigger) return;
    this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  setHeaderBottomVar() {
    const wrap = this.headerWrapper || document.querySelector('.header-wrapper');
    if (!wrap) return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(wrap.getBoundingClientRect().bottom)}px`
    );
  }

  close() {
    this.clearEnterTimer();
    this.clearLeaveTimer();
    this.classList.remove('mega-menu--interacting');
    this.setAttribute('data-force-closed', '');
    this.setExpanded(false);
    this.setHeaderPreventHide(false);
  }
}

customElements.define('header-mega-menu', HeaderMegaMenu);
